import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SwitchRow } from '@/components/ui/switch';
import { RiskRuleKey, RiskSeverity, apiError, type RiskRuleListItem, type UpdateRiskRuleInput } from './lib';

/**
 * Retune one rule. `enabled`/`windowMinutes`/threshold fields all default to
 * the row's OWN current value (or the shipped default on a never-configured
 * rule) — never re-sent from a form default, which is exactly the bug
 * `RiskRulesService.upsert`'s own doc comment describes: a manager reaching
 * for one knob (say, severity) must not silently reset every threshold
 * already tuned, or silently re-enable a rule someone switched off.
 */
export default function RuleFormDialog({
  rule,
  onClose,
  onSaved,
}: {
  rule: RiskRuleListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { format, toMoney } = useCurrency(null);
  const isAfterHours = rule.rule === RiskRuleKey.AFTER_HOURS_ACTIVITY;

  const [enabled, setEnabled] = useState(rule.enabled);
  const [windowMinutes, setWindowMinutes] = useState(String(rule.windowMinutes));
  const [countEnabled, setCountEnabled] = useState(rule.countThreshold !== null);
  const [countThreshold, setCountThreshold] = useState(String(rule.countThreshold ?? 5));
  const [amountEnabled, setAmountEnabled] = useState(rule.amountThreshold !== null);
  const [amountThreshold, setAmountThreshold] = useState(rule.amountThreshold ?? '');
  const [severity, setSeverity] = useState<RiskSeverity>(rule.severity);
  const [startHour, setStartHour] = useState(String(rule.params?.startHour ?? 23));
  const [endHour, setEndHour] = useState(String(rule.params?.endHour ?? 6));
  const [saving, setSaving] = useState(false);

  const noArm = enabled && !countEnabled && !amountEnabled;

  const save = async () => {
    if (noArm) return;
    setSaving(true);
    try {
      const body: UpdateRiskRuleInput = {
        enabled,
        windowMinutes: Number(windowMinutes),
        countThreshold: countEnabled ? Number(countThreshold) : null,
        amountThreshold: amountEnabled ? toMoney(amountThreshold || '0') : null,
        severity,
        ...(isAfterHours ? { startHour: Number(startHour), endHour: Number(endHour) } : {}),
      };
      await api.patch(`/risk/rules/${rule.rule}`, body);
      toast.success(t('risk.rules.saved'));
      onSaved();
    } catch (e) {
      toast.error(apiError(e, t('risk.rules.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(`risk.rule.${rule.rule}`)}
      description={t(`risk.ruleDescription.${rule.rule}`)}
      className="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SwitchRow
          label={t('risk.rules.enabled')}
          hint={t('risk.rules.enabledHint')}
          checked={enabled}
          onChange={setEnabled}
        />

        <Field label={t('risk.rules.window')} hint={t('risk.rules.windowHint')}>
          <Input
            type="number"
            min={5}
            max={43_200}
            value={windowMinutes}
            onChange={(e) => setWindowMinutes(e.target.value)}
          />
        </Field>

        <div className="space-y-2 rounded-lg border p-3">
          <SwitchRow
            label={t('risk.rules.countArm')}
            checked={countEnabled}
            onChange={setCountEnabled}
          />
          {countEnabled && (
            <Field label={t('risk.rules.countThreshold')}>
              <Input
                type="number"
                min={0}
                value={countThreshold}
                onChange={(e) => setCountThreshold(e.target.value)}
              />
            </Field>
          )}

          <SwitchRow
            label={t('risk.rules.amountArm')}
            checked={amountEnabled}
            onChange={setAmountEnabled}
          />
          {amountEnabled && (
            <Field
              label={t('risk.rules.amountThreshold')}
              hint={t('risk.rules.amountThresholdHint', { example: format('2500') })}
            >
              <Input
                inputMode="decimal"
                value={amountThreshold}
                onChange={(e) => setAmountThreshold(e.target.value)}
              />
            </Field>
          )}
          {noArm && (
            <p className="text-xs text-red-600 dark:text-red-400">{t('risk.rules.noArmError')}</p>
          )}
        </div>

        <Field label={t('risk.rules.severity')}>
          <Select value={severity} onChange={(e) => setSeverity(e.target.value as RiskSeverity)}>
            {Object.values(RiskSeverity).map((s) => (
              <option key={s} value={s}>
                {t(`risk.severity.${s}`)}
              </option>
            ))}
          </Select>
        </Field>

        {isAfterHours && (
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('risk.rules.startHour')} hint={t('risk.rules.startHourHint')}>
              <Input type="number" min={0} max={23} value={startHour} onChange={(e) => setStartHour(e.target.value)} />
            </Field>
            <Field label={t('risk.rules.endHour')} hint={t('risk.rules.endHourHint')}>
              <Input type="number" min={0} max={23} value={endHour} onChange={(e) => setEndHour(e.target.value)} />
            </Field>
          </div>
        )}
      </div>
    </Dialog>
  );
}
